import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  Bot,
  Sparkles,
  Send,
  Copy,
  Check,
  ThumbsUp,
  ThumbsDown,
  Inbox,
  BookOpen,
  CornerDownLeft,
  Plus,
  ShieldCheck,
} from 'lucide-react';
import { ApiError } from '@/lib/apiClient';
import { useFoxitAsk, useFoxitFeedback } from '@/queries/foxit.queries';
import { useAppSelector } from '@/store/hooks';
import type { Citation } from '@/lib/types';
import { Button, cx } from '@/components/ui';
import { Markdown } from '@/components/Markdown';

type TurnStatus = 'pending' | 'done' | 'error';

interface Turn {
  id: string;
  question: string;
  status: TurnStatus;
  answer: string | null;
  deflected: boolean;
  citations: Citation[];
  handoff: { category: string; inbox: string };
  traceId?: string | null;
  error?: string;
  feedback?: 'up' | 'down';
}

const SUGGESTIONS: { icon: typeof Sparkles; text: string }[] = [
  { icon: Sparkles, text: 'How does the employee referral program work?' },
  { icon: BookOpen, text: 'How do I file an expense in Zoho?' },
  { icon: ShieldCheck, text: 'What is AI-SPM?' },
  { icon: Sparkles, text: "What is Saint Fox's brand voice?" },
];

const uid = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

const prettyCategory = (c: string) =>
  c ? c.replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()) : 'the right team';

/** Renders the assistant's markdown answer with app-styled formatting. */
function AnswerText({ text }: { text: string }) {
  return <Markdown>{text}</Markdown>;
}

function BotAvatar() {
  return (
    <span className="grid place-items-center h-8 w-8 shrink-0 rounded-lg bg-primary text-primary-foreground shadow-sm">
      <Bot className="h-4 w-4" />
    </span>
  );
}

export function FoxitView() {
  const user = useAppSelector((s) => s.auth.user);
  const token = useAppSelector((s) => s.auth.token);
  const initials = (user?.displayName || user?.email || '?').trim()[0]?.toUpperCase() ?? '?';

  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedQuestionId, setCopiedQuestionId] = useState<string | null>(null);

  const askMutation = useFoxitAsk();
  const feedbackMutation = useFoxitFeedback();
  const busy = askMutation.isPending;

  const scrollRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Keep the latest message in view, scrolled up clear of the composer.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [turns]);

  // Auto-grow the composer textarea (up to a max height).
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [question]);

  async function send(q: string) {
    const text = q.trim();
    if (!text || busy) return;
    const id = uid();
    setQuestion('');
    setTurns((t) => [
      ...t,
      {
        id,
        question: text,
        status: 'pending',
        answer: null,
        deflected: false,
        citations: [],
        handoff: { category: '', inbox: '' },
      },
    ]);

    const history = turns
      .filter((t) => t.status === 'done' && !t.error && !t.deflected)
      .flatMap((t) => [
        { role: 'user' as const, content: t.question },
        { role: 'assistant' as const, content: t.answer || '' },
      ]);

    try {
      const r = await askMutation.mutateAsync({ question: text, history });
      const sanitizeAnswer = (raw: string | null) => {
        if (!raw) return '';
        const trimmed = raw.trim();
        if (trimmed.startsWith('{') && (trimmed.includes('"response"') || trimmed.includes('"answered"'))) {
          try {
            const parsed = JSON.parse(trimmed);
            if (typeof parsed.response === 'string' && parsed.response.trim()) {
              return parsed.response.trim();
            }
          } catch {
            const m = trimmed.match(/"response"\s*:\s*"([\s\S]*?)"\s*(?:,|\})/s) ||
                      trimmed.match(/"response"\s*:\s*"((?:[^"\\]|\\.)*)"/s);
            if (m && m[1]) {
              return m[1].replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\').trim();
            }
          }
          return 'FoxIT is currently processing your request. Please ask your question again.';
        }
        return raw;
      };
      const cleanAnswer = sanitizeAnswer(r.answer);
      setTurns((prev) =>
        prev.map((x) =>
          x.id === id
            ? {
                ...x,
                status: 'done',
                answer: cleanAnswer,
                deflected: r.deflected,
                citations: r.citations,
                handoff: r.handoff,
                traceId: r.traceId,
              }
            : x,
        ),
      );
    } catch (err) {
      setTurns((prev) =>
        prev.map((x) =>
          x.id === id
            ? {
                ...x,
                status: 'error',
                error: err instanceof ApiError ? err.message : 'FoxIT is unavailable right now.',
              }
            : x,
        ),
      );
    }
  }

  async function rate(turn: Turn, value: 'up' | 'down') {
    if (turn.feedback) return;
    setTurns((prev) => prev.map((x) => (x.id === turn.id ? { ...x, feedback: value } : x)));
    feedbackMutation
      .mutateAsync({
        question: turn.question,
        rating: value === 'up' ? 5 : 2,
        deflected: turn.deflected,
        traceId: turn.traceId,
      })
      .catch(() => undefined);
  }

  async function copy(turn: Turn) {
    if (!turn.answer) return;
    try {
      await navigator.clipboard.writeText(turn.answer);
      setCopiedId(turn.id);
      setTimeout(() => setCopiedId((c) => (c === turn.id ? null : c)), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  async function copyQuestion(turn: Turn) {
    try {
      await navigator.clipboard.writeText(turn.question);
      setCopiedQuestionId(turn.id);
      setTimeout(() => setCopiedQuestionId((c) => (c === turn.id ? null : c)), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send(question);
    }
  }

  const empty = turns.length === 0;
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  }, []);

  const composer = (
    <div className="mx-auto w-full max-w-3xl">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void send(question);
        }}
        className={cx(
          'flex items-end gap-2 rounded-3xl border bg-card p-2 pl-4 shadow-lg transition-colors',
          'border-border focus-within:border-primary/60',
        )}
      >
        <textarea
          ref={taRef}
          rows={1}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask FoxIT anything…"
          className="flex-1 resize-none bg-transparent py-2.5 text-[15px] leading-relaxed text-foreground outline-none placeholder:text-muted-foreground max-h-40"
        />
        <Button type="submit" disabled={busy || !question.trim()} size="md" className="shrink-0 rounded-2xl">
          <Send className="h-4 w-4" />
          <span className="hidden sm:inline">Send</span>
        </Button>
      </form>
      <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-[11px] text-muted-foreground">
        <CornerDownLeft className="h-3 w-3" />
        Enter to send · Shift+Enter for a new line
      </p>
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col -mb-4 sm:-mb-6 md:-mb-8">
      {!empty && (
        <div className="flex justify-end pb-3">
          <button
            onClick={() => setTurns([])}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New chat</span>
          </button>
        </div>
      )}

      {/* Chat region: messages scroll underneath a sticky, centered composer. */}
      <div className="relative min-h-0 flex-1">
        <div ref={scrollRef} className="absolute inset-0 overflow-y-auto" aria-live="polite">
          {empty ? (
            <div className="flex h-full flex-col items-center justify-center px-2 pb-28 text-center">
              <span className="grid place-items-center h-14 w-14 rounded-2xl bg-primary text-primary-foreground shadow-lg mb-5">
                <Bot className="h-7 w-7" />
              </span>
              <h2 className="text-2xl font-bold tracking-tight text-foreground">
                {greeting}
                {user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}.
              </h2>
              <p className="mt-2 max-w-md text-muted-foreground">
                I answer questions about Saint Fox policies and our internal knowledge base — HR,
                IT, finance, onboarding, and security. Ask me about a company policy or process.
              </p>
              <div className="mt-7 grid w-full max-w-2xl gap-3 sm:grid-cols-2">
                {SUGGESTIONS.map(({ icon: Ic, text }) => (
                  <button
                    key={text}
                    onClick={() => void send(text)}
                    className="group flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
                  >
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <Ic className="h-4 w-4" />
                    </span>
                    <span className="text-sm text-foreground">{text}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto flex max-w-3xl flex-col gap-6 px-0.5 pt-5 pb-44">
              {turns.map((t) => (
                <div key={t.id} className="animate-rise flex flex-col gap-3">
                  {/* User message */}
                  <div className="flex flex-col items-end gap-1">
                    <div className="flex items-start justify-end gap-2.5">
                      <div className="max-w-[82%] rounded-2xl rounded-br-md bg-primary px-4 py-2.5 text-[15px] leading-relaxed text-primary-foreground shadow-sm">
                        {t.question}
                      </div>
                      <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-secondary text-secondary-foreground text-xs font-bold border border-border">
                        {initials}
                      </span>
                    </div>
                    {/* Copy question button */}
                    <div className="mr-11 flex items-center text-muted-foreground">
                      <button
                        onClick={() => void copyQuestion(t)}
                        title={copiedQuestionId === t.id ? 'Copied!' : 'Copy question'}
                        className="grid h-7 w-7 place-items-center rounded-md hover:bg-accent hover:text-foreground transition-colors"
                      >
                        {copiedQuestionId === t.id ? (
                          <Check className="h-3.5 w-3.5 text-success" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Assistant message */}
                  <div className="flex items-start gap-2.5">
                    <BotAvatar />
                    <div className="min-w-0 flex-1">
                      {t.status === 'pending' ? (
                        <div className="inline-flex items-center gap-2 rounded-2xl rounded-tl-md bg-card border border-border px-4 py-3 text-muted-foreground">
                          <span className="flex gap-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-soft" />
                            <span
                              className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-soft"
                              style={{ animationDelay: '0.15s' }}
                            />
                            <span
                              className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-soft"
                              style={{ animationDelay: '0.3s' }}
                            />
                          </span>
                          <span className="text-sm">FoxIT is thinking…</span>
                        </div>
                      ) : t.status === 'error' ? (
                        <div className="rounded-2xl rounded-tl-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                          {t.error}
                        </div>
                      ) : t.deflected ? (
                        <div className="rounded-2xl rounded-tl-md border border-border bg-card p-4">
                          <div className="flex items-start gap-3">
                            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-warning/15 text-warning">
                              <Inbox className="h-4 w-4" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-foreground">
                                <AnswerText text={t.answer || "I couldn't find details regarding this topic in our policy files."} />
                              </div>
                              <p className="mt-2 text-xs text-muted-foreground">
                                You can email the{' '}
                                <span className="font-semibold text-foreground">
                                  {prettyCategory(t.handoff.category)}
                                </span>{' '}
                                team directly:
                              </p>
                              {t.handoff.inbox && (
                                <a
                                  href={`mailto:${t.handoff.inbox}?subject=${encodeURIComponent(
                                    'FoxIT question',
                                  )}&body=${encodeURIComponent(t.question)}`}
                                  className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-accent px-2.5 py-1.5 text-xs font-medium text-accent-foreground hover:brightness-95 transition"
                                >
                                  <Inbox className="h-3.5 w-3.5" />
                                  {t.handoff.inbox}
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-2xl rounded-tl-md border border-border bg-card p-4">
                          <AnswerText text={t.answer ?? ''} />
                          {t.citations && t.citations.length > 0 && (
                            <div className="mt-3.5 pt-3 border-t border-border flex flex-wrap items-center gap-1.5">
                              <span className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1 mr-1 shrink-0">
                                <BookOpen className="h-3.5 w-3.5" /> Sources:
                              </span>
                              {t.citations.map((cite, i) => (
                                cite.url ? (
                                  <a
                                    key={cite.url || i}
                                    href={cite.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground border border-border/40 hover:bg-accent/80 hover:text-foreground transition-colors cursor-pointer"
                                    title={`Visit: ${cite.title} (${prettyCategory(cite.category)})`}
                                  >
                                    {cite.title}
                                  </a>
                                ) : cite.kbId && cite.kbId !== 'kb_seed' ? (
                                  <a
                                    key={cite.kbId || i}
                                    href={`/api/library/files/${cite.kbId}/download?token=${token}`}
                                    download
                                    className="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground border border-border/40 hover:bg-accent/80 hover:text-foreground transition-colors cursor-pointer"
                                    title={`Download: ${cite.title} (${prettyCategory(cite.category)})`}
                                  >
                                    {cite.title}
                                  </a>
                                ) : (
                                  <span
                                    key={cite.kbId || cite.title || i}
                                    className="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground border border-border/40 transition-colors"
                                    title={`${cite.title} (${prettyCategory(cite.category)})`}
                                  >
                                    {cite.title}
                                  </span>
                                )
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action bar */}
                      {t.status === 'done' && !t.deflected && t.answer && (
                        <div className="mt-2 flex items-center gap-1 pl-1 text-muted-foreground">
                          <button
                            onClick={() => void copy(t)}
                            title={copiedId === t.id ? 'Copied!' : 'Copy answer'}
                            className="grid h-7 w-7 place-items-center rounded-md hover:bg-accent hover:text-foreground transition-colors"
                          >
                            {copiedId === t.id ? (
                              <Check className="h-3.5 w-3.5 text-success" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <span className="mx-1 h-4 w-px bg-border" />
                          {t.feedback ? (
                            <span className="inline-flex items-center gap-1 px-2 text-xs text-success">
                              <Check className="h-3.5 w-3.5" /> Thanks for the feedback
                            </span>
                          ) : (
                            <>
                              <button
                                onClick={() => void rate(t, 'up')}
                                title="Helpful"
                                className="grid h-7 w-7 place-items-center rounded-md hover:bg-accent hover:text-foreground transition-colors"
                              >
                                <ThumbsUp className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => void rate(t, 'down')}
                                title="Not helpful"
                                className="grid h-7 w-7 place-items-center rounded-md hover:bg-accent hover:text-foreground transition-colors"
                              >
                                <ThumbsDown className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Fade so messages dissolve as they scroll under the composer */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-background via-background/85 to-transparent" />

        {/* Sticky composer, centered like Claude.ai */}
        <div className="absolute inset-x-0 bottom-0 px-0.5 pb-3">{composer}</div>
      </div>
    </div>
  );
}
